import { useCallback, useEffect, useRef } from 'react';

export default function useDialogFocus() {
  const ref = useRef<HTMLDivElement | null>(null);
  const frame = useRef<number>();

  const cancel = useCallback(() => {
    if (frame.current !== undefined) cancelAnimationFrame(frame.current);
    frame.current = undefined;
  }, []);

  useEffect(() => cancel, [cancel]);

  const onOpenAutoFocus = useCallback(
    (event: Event) => {
      event.preventDefault();
      cancel();
      const content = ref.current;

      // Let the dialog and scroll lock paint before focus forces another layout calculation.
      frame.current = requestAnimationFrame(() => {
        frame.current = requestAnimationFrame(() => {
          frame.current = undefined;
          if (
            content?.isConnected &&
            content === ref.current &&
            content.dataset.state === 'open' &&
            !content.contains(document.activeElement)
          ) {
            content.focus({ preventScroll: true });
          }
        });
      });
    },
    [cancel]
  );

  return { ref, onOpenAutoFocus, onCloseAutoFocus: cancel };
}
